import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Pagination,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import {
  websiteFormsApi,
  OpenTableReservation,
  PaginatedOpenTableReservationsResponse,
} from '../services/websiteFormsService.ts';

const OpenTableReservationsPage: React.FC = () => {
  const { sessionToken } = useAuth();
  const { showError } = useSnackbar();
  const [reservations, setReservations] = useState<OpenTableReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [emailSearch, setEmailSearch] = useState('');
  const [emailSearchInput, setEmailSearchInput] = useState('');

  const fetchReservations = async (pageNum: number = 1, email?: string) => {
    if (!sessionToken) return;

    setLoading(true);

    try {
      const response: PaginatedOpenTableReservationsResponse =
        await websiteFormsApi.getOpenTableReservations(
          { page: pageNum, limit, email },
          sessionToken
        );

      setReservations(response.data);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch open table reservations';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    fetchReservations(value, emailSearch || undefined);
  };

  const handleSearch = () => {
    setEmailSearch(emailSearchInput);
    setPage(1);
    fetchReservations(1, emailSearchInput || undefined);
  };

  const handleClearSearch = () => {
    setEmailSearchInput('');
    setEmailSearch('');
    setPage(1);
    fetchReservations(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString.slice(0, 10) + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const consentIcon = (value: boolean, label: string) => (
    <Tooltip title={`${label}: ${value ? 'Yes' : 'No'}`}>
      {value ? (
        <CheckCircleIcon fontSize="small" color="success" />
      ) : (
        <CancelIcon fontSize="small" color="disabled" />
      )}
    </Tooltip>
  );

  if (loading && reservations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Open Table Reservations
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => fetchReservations(page, emailSearch || undefined)}
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Large party requests (15+ guests) submitted from the website.
      </Typography>

      {/* Search Bar */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            label="Search by Email"
            value={emailSearchInput}
            onChange={(e) => setEmailSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter email address..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: emailSearchInput && (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            sx={{ minWidth: '100px' }}
          >
            Search
          </Button>
        </Box>
        {emailSearch && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing results for: <strong>{emailSearch}</strong>
          </Typography>
        )}
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #7f0000' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {[
                  { label: 'Name', align: 'left' },
                  { label: 'Email', align: 'left' },
                  { label: 'Mobile', align: 'left' },
                  { label: 'Party Size', align: 'right' },
                  { label: 'Date', align: 'left' },
                  { label: 'Time', align: 'left' },
                  { label: 'Seating', align: 'left' },
                  { label: 'Occasion', align: 'left' },
                  { label: 'Marketing', align: 'center' },
                  { label: 'SMS', align: 'center' },
                  { label: 'Submitted', align: 'left' },
                ].map(({ label, align }) => (
                  <TableCell
                    key={label}
                    align={align as any}
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      border: '2px solid #7f0000',
                      py: 1.5,
                      px: 1.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reservations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {emailSearch
                        ? 'No reservations found matching the search criteria'
                        : 'No reservations found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reservations.map((reservation) => (
                  <TableRow key={reservation.id} hover>
                    <TableCell>{reservation.firstName} {reservation.lastName}</TableCell>
                    <TableCell>{reservation.email}</TableCell>
                    <TableCell>{reservation.mobileNumber}</TableCell>
                    <TableCell align="right">{reservation.partySize}</TableCell>
                    <TableCell>{formatDate(reservation.reservationDate)}</TableCell>
                    <TableCell>{reservation.reservationTime}</TableCell>
                    <TableCell>{reservation.seatingPreference}</TableCell>
                    <TableCell>
                      {reservation.occasion ? (
                        <Chip label={reservation.occasion} size="small" />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="center">{consentIcon(reservation.marketingOptIn, 'Marketing Opt-In')}</TableCell>
                    <TableCell align="center">{consentIcon(reservation.smsConsent, 'SMS Consent')}</TableCell>
                    <TableCell>{formatDateTime(reservation.createdAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {reservations.length} of {total} reservation
              {total !== 1 ? 's' : ''}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OpenTableReservationsPage;
